import { useTranslation } from 'react-i18next';
import { decimal } from '@/lib/format';

export interface ChartPoint { x: number; y: number }

/** Small read-only plot. The point table supplies exact values to keyboard and screen-reader users. */
export function MeasurementChart({ points, xLabel, yLabel, xUnit, yUnit, formatX }: {
  points: ChartPoint[];
  xLabel: string;
  yLabel: string;
  xUnit: string;
  yUnit: string;
  formatX?: (x: number) => string;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const valid = points.filter(({ x, y }) => Number.isFinite(x) && Number.isFinite(y));
  if (!valid.length) return <p className="text-sm text-ink-muted">{t('personDetail.noCurve')}</p>;
  const xs = valid.map((item) => item.x);
  const ys = valid.map((item) => item.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const left = 66;
  const right = 465;
  const top = 16;
  const bottom = 145;
  const px = (x: number) => minX === maxX ? (left + right) / 2 : left + (x - minX) / (maxX - minX) * (right - left);
  const py = (y: number) => minY === maxY ? (top + bottom) / 2 : bottom - (y - minY) / (maxY - minY) * (bottom - top);
  const path = valid.map((point, index) => `${index ? 'L' : 'M'} ${px(point.x)} ${py(point.y)}`).join(' ');
  const xText = (value: number) => formatX?.(value) ?? `${decimal(value, language)}${xUnit ? ` ${xUnit}` : ''}`;
  const yText = (value: number) => `${decimal(value, language)}${yUnit ? ` ${yUnit}` : ''}`;
  return <div>
    <svg viewBox="0 0 480 174" role="img" aria-label={t('personDetail.curveFor', { x: xLabel, y: yLabel })} className="h-auto w-full text-brand">
      <path d={`M ${left} ${top} V ${bottom} H ${right}`} fill="none" stroke="currentColor" className="text-line" />
      {minY === maxY ? <text x={left - 9} y={(top + bottom) / 2 + 4} textAnchor="end" fill="currentColor" fontSize="14" className="text-ink-muted">{decimal(minY, language)}</text> : <>
        <path d={`M ${left} ${top} H ${right}`} fill="none" stroke="currentColor" strokeDasharray="3 4" className="text-line" />
        <text x={left - 9} y={top + 4} textAnchor="end" fill="currentColor" fontSize="14" className="text-ink-muted">{decimal(maxY, language)}</text>
        <text x={left - 9} y={bottom + 4} textAnchor="end" fill="currentColor" fontSize="14" className="text-ink-muted">{decimal(minY, language)}</text>
      </>}
      {valid.length > 1 ? <path d={path} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" /> : null}
      {valid.length === 1 ? <circle cx={px(valid[0].x)} cy={py(valid[0].y)} r="4" fill="currentColor" /> : null}
    </svg>
    <div className="mt-1 flex justify-between gap-2 text-xs text-ink-muted" style={{ marginLeft: `${left / 480 * 100}%`, marginRight: `${(480 - right) / 480 * 100}%` }}><span className="break-words">{xText(minX)}</span>{minX !== maxX ? <span className="break-words text-right">{xText(maxX)}</span> : null}</div>
    <p className="mt-1 text-xs text-ink-subtle">{xLabel} · {yLabel}: {yText(minY)}{minY !== maxY ? ` – ${yText(maxY)}` : ''}</p>
    <details className="mt-2 text-sm text-ink"><summary className="min-h-11 cursor-pointer py-2 text-brand">{t('personDetail.dataPoints', { count: valid.length })}</summary>
      <div className="max-h-48 overflow-auto rounded-lg border border-line"><table className="w-full text-left"><thead><tr className="border-b border-line"><th scope="col" className="px-3 py-2 font-medium">{xLabel}</th><th scope="col" className="px-3 py-2 font-medium">{yLabel}</th></tr></thead><tbody>{valid.map((point, index) => <tr key={`${point.x}:${point.y}:${index}`} className="border-b border-line last:border-0"><td className="px-3 py-2 tabular-nums">{xText(point.x)}</td><td className="px-3 py-2 tabular-nums">{yText(point.y)}</td></tr>)}</tbody></table></div>
    </details>
  </div>;
}
